import {
    ENDPOINTS,
    QUERYKEYS,
    ENDPOINT_METHODS,
    createEndpointUrl,
} from "./MonitoringApiConstants";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFetchWrapper } from "./WebUtils";
import { localFetch } from "./LocalMonitoringFetch";
// @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
import { useMsal } from "@azure/msal-react";

const DEBUG = true;

const NEWS_QUEUE_BACKEND_URL = process.env.REACT_APP_NEWS_QUEUE_BACKEND_URL;

const fetchWrapper = createFetchWrapper({
    createEndpointUrl,
    localFetch,
    endpointMethods: ENDPOINT_METHODS,
    backendUrl: NEWS_QUEUE_BACKEND_URL,
});

export function useGetMediaSummary({ enabled = true, staleTime = 60_000 } = {}) {
    // @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
    const msal = useMsal();

    return useQuery({
        queryKey: [QUERYKEYS.getMediaSummary, {}],
        queryFn: async ({ queryKey }) => {
            if (DEBUG) console.log("Api :: getMediaSummary");

            const [_key] = queryKey;

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.getMediaSummary,
                parameters: {},
                msal,
            });

            return { queryKey: _key, result };
        },
        enabled,
        staleTime,
        refetchOnWindowFocus: false,
    });
}

export function useGetAllSourcesSummary({ enabled = true, staleTime = 60_000 } = {}) {
    const msal = useMsal();

    return useQuery({
        queryKey: [QUERYKEYS.getAllSourcesSummary, {}],
        queryFn: async ({ queryKey }) => {
            if (DEBUG) console.log("Api :: getAllSourcesSummary");

            const [_key] = queryKey;

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.getAllSourcesSummary,
                parameters: {},
                msal,
            });

            return { queryKey: _key, result };
        },
        enabled,
        staleTime,
        refetchOnWindowFocus: false,
    });
}

export function useGetMonitoringAlerts({ enabled = true, staleTime = 60_000 } = {}) {
    const msal = useMsal();

    return useQuery({
        queryKey: [QUERYKEYS.getMonitoringAlerts, {}],
        queryFn: async ({ queryKey }) => {
            if (DEBUG) console.log("Api :: getMonitoringAlerts");

            const [_key] = queryKey;

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.getMonitoringAlerts,
                parameters: {},
                msal,
            });

            return { queryKey: _key, result };
        },
        enabled,
        staleTime,
        refetchOnWindowFocus: false,
    });
}

export function useSuppressMonitoringAlert({ onSuccess, onError } = {}) {
    const msal = useMsal();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ alertId, days }) => {
            if (DEBUG) console.log("Api :: suppressMonitoringAlert", { alertId, days });

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.suppressMonitoringAlert,
                parameters: { alertId },
                body: { days },
                msal,
            });

            return { result };
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [QUERYKEYS.getMonitoringAlerts] });
            if (onSuccess) onSuccess(data);
        },
        onError: (error) => {
            if (onError) onError(error);
        },
    });
}

export function useClearMonitoringAlertSuppression({ onSuccess, onError } = {}) {
    const msal = useMsal();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ alertId }) => {
            if (DEBUG) console.log("Api :: clearMonitoringAlertSuppression", { alertId });

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.clearMonitoringAlertSuppression,
                parameters: { alertId },
                msal,
            });

            return { result };
        },
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: [QUERYKEYS.getMonitoringAlerts] });
            if (onSuccess) onSuccess(data);
        },
        onError: (error) => {
            if (onError) onError(error);
        },
    });
}

export function useGetMediaSourcesSummary({ mediaId, enabled = true, staleTime = 60_000 } = {}) {
    // @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
    const msal = useMsal();

    return useQuery({
        queryKey: [QUERYKEYS.getMediaSourcesSummary, { mediaId }],
        queryFn: async ({ queryKey }) => {
            if (DEBUG) console.log("Api :: getMediaSourcesSummary");

            const [_key, parameters] = queryKey;

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.getMediaSourcesSummary,
                parameters,
                msal,
            });

            return { queryKey: _key, result };
        },
        enabled: enabled && Boolean(mediaId),
        staleTime,
        refetchOnWindowFocus: false,
    });
}

export function useGetSourceRuns({ sourceId, days = 14, enabled = true, staleTime = 60_000 } = {}) {
    // @TEMPLATE_@MSAL - Uncomment this line to use MSAL for authentication.
    const msal = useMsal();

    return useQuery({
        queryKey: [QUERYKEYS.getSourceRuns, { sourceId, days }],
        queryFn: async ({ queryKey }) => {
            if (DEBUG) console.log("Api :: getSourceRuns");

            const [_key, parameters] = queryKey;

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.getSourceRuns,
                parameters,
                msal,
            });

            return { queryKey: _key, result };
        },
        enabled: enabled && Boolean(sourceId),
        staleTime,
        refetchOnWindowFocus: false,
    });
}

export function useGetRunPipelineItems({ runId, enabled = true, staleTime = 60_000 } = {}) {
    const msal = useMsal();

    return useQuery({
        queryKey: [QUERYKEYS.getRunPipelineItems, { runId }],
        queryFn: async ({ queryKey }) => {
            if (DEBUG) console.log("Api :: getRunPipelineItems");

            const [_key, parameters] = queryKey;

            const result = await fetchWrapper({
                endpoint: ENDPOINTS.getRunPipelineItems,
                parameters,
                msal,
            });

            return { queryKey: _key, result };
        },
        enabled: enabled && Boolean(runId),
        staleTime,
        refetchOnWindowFocus: false,
    });
}
